import { useState, useEffect, useRef } from 'react';
import { 
    PaperAirplaneIcon,
    PhoneIcon,
    CalendarDaysIcon,
    ClockIcon,
    UserCircleIcon,
    XMarkIcon,
    ExclamationCircleIcon,
    BuildingOffice2Icon,
    ClipboardDocumentListIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import notify from '../../Utils/notify';
import Loader from '../../Layout/Loaders/Loader';
import MediaMessage from './MediaMessage';
import AppointmentDetailModal from './AppointmentDetailModal';

// Palomita simple (WhatsApp): mensaje enviado
const SingleCheck = ({ className }) => (
    <svg className={className} viewBox="0 0 16 15" fill="currentColor">
        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.032l6.272-8.048a.366.366 0 0 0-.064-.512z"/>
    </svg>
);

// Palomita doble (WhatsApp): entregado (gris) o leído (azul)
const DoubleCheck = ({ className }) => (
    <svg className={className} viewBox="0 0 16 15" fill="currentColor">
        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.032l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.032l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
    </svg>
);

// Ícono de estado de entrega según el último status reportado por el webhook de WhatsApp
const DeliveryStatusIcon = ({ status }) => {
    if (status === 'failed') {
        return <ExclamationCircleIcon className="w-4 h-4 text-red-500" />;
    }
    if (status === 'read') {
        return <DoubleCheck className="w-4 h-4 text-[#53bdeb]" />;
    }
    if (status === 'delivered') {
        return <DoubleCheck className="w-4 h-4 text-gray-500" />;
    }
    // 'sent' o sin estado aún reportado por el webhook
    return <SingleCheck className="w-4 h-4 text-gray-500" />;
};

// Una fecha 'YYYY-MM-DD' se interpreta como UTC y en México se ve un día antes; se construye en hora local
const formatFechaCita = (fecha) => {
    const [y, m, d] = String(fecha ?? '').slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return '—';
    return new Date(y, m - 1, d).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ChatView = ({ phoneNumber, patientName, appointment, messages, onSendMessage, loading, canReply = true }) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Auto scroll al final cuando hay nuevos mensajes
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [message]);

    const handleSend = async () => {
        if (!message.trim() || sending || !canReply) return;

        setSending(true);
        try {
            await onSendMessage(phoneNumber, message.trim());
            setMessage('');
            // Reset textarea height
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Hoy';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Ayer';
        } else {
            return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        }
    };

    // Agrupar mensajes por fecha
    const groupMessagesByDate = () => {
        const groups = {};
        messages.forEach((msg) => {
            const dateKey = new Date(msg.timestamp).toDateString();
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(msg);
        });
        return groups;
    };

    if (!phoneNumber) {
        return (
            <div className="h-full flex flex-col items-center justify-center bg-[#c1f0f8] text-gray-500">
                <div className="text-center px-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">WhatsApp Chat</h3>
                    <h5 className="text-blue-600">Selecciona una conversación para comenzar</h5>
                    <Loader />
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#25D366] mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500">Cargando mensajes...</p>
                </div>
            </div>
        );
    }

    const messageGroups = groupMessagesByDate();

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="bg-[#11cdef] px-4 py-2 flex items-center gap-3 shadow-md">
                <div className="w-8 h-8 rounded-full bg-white/20 bg-gradient-to-br from-[#11cdef] to-[#3862ee] flex items-center justify-center shrink-0">
                    <UserCircleIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-sm truncate leading-tight">{patientName}</h3>
                    <p className="text-[11px] text-white/70 font-mono leading-tight">{phoneNumber}</p>
                </div>
            </div>

            {/* Datos de la cita vinculada (o aviso si no hay ninguna) */}
            {appointment ? (
                <button
                    type="button"
                    onClick={() => setShowAppointmentModal(true)}
                    className="w-full text-left bg-white hover:bg-gray-50 border-b border-gray-200 px-4 py-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 transition-colors cursor-pointer"
                >
                    <span className="flex items-center gap-1">
                        <CalendarDaysIcon className="h-3.5 w-3.5 text-[#11cdef]" />
                        <span className="font-semibold">Fecha de la cita:</span> {formatFechaCita(appointment.fecha)}
                    </span>
                    <span className="flex items-center gap-1">
                        <ClockIcon className="h-3.5 w-3.5 text-[#11cdef]" />
                        {appointment.hora || '—'}
                    </span>
                    {appointment.sucursal && (
                        <span className="flex items-center gap-1">
                            <BuildingOffice2Icon className="h-3.5 w-3.5 text-[#11cdef]" />
                           <span className="font-semibold">Sucursal:</span>  {appointment.sucursal?.nombre_sucursal || '—'}
                        </span>
                    )}
                    {appointment.doctor && (
                        <span className="flex items-center gap-1">
                            <UserIcon className="h-3.5 w-3.5 text-[#11cdef]" />
                            <span className="font-semibold">Doctor:</span> {appointment.doctor || '—'}
                        </span>
                    )}
                    {appointment.servicios?.length > 0 && (
                        <span className="flex items-center gap-1 min-w-0 max-w-full">
                            <ClipboardDocumentListIcon className="h-3.5 w-3.5 text-[#11cdef] shrink-0" />
                            <span className="truncate leading-none">{appointment.servicios.join(', ')}</span>
                        </span>
                    )}
                </button>
            ) : (
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-1.5 text-xs text-gray-400 italic">
                    Sin cita vinculada a este número
                </div>
            )}

            {/* Modal de detalle de la cita */}
            {showAppointmentModal && (
                <AppointmentDetailModal
                    appointment={appointment}
                    onClose={() => setShowAppointmentModal(false)}
                />
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto bg-[#c1f0f8] p-4 space-y-3">
                {Object.entries(messageGroups).map(([dateKey, msgs]) => (
                    <div key={dateKey}>
                        {/* Date Divider */}
                        <div className="flex items-center justify-center my-4">
                            <div className="bg-white/90 rounded-lg px-3 py-1 shadow-sm">
                                <p className="text-xs font-medium text-gray-600">
                                    {formatDate(msgs[0].timestamp)}
                                </p>
                            </div>
                        </div>

                        {/* Messages */}
                        {msgs.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[75%] rounded-lg px-3 my-1 shadow-sm
                                        ${msg.direction === 'outgoing'
                                            ? 'bg-[#D9FDD3]'
                                            : 'bg-white'
                                        }
                                    `}
                                >
                                    {/* Mensaje del usuario (solo para outgoing) */}
                                    {msg.direction === 'outgoing' && msg.sent_by_user && (
                                        <p className="text-xs font-semibold text-[#128C7E] mb-1">
                                            {msg.sent_by_user.name}
                                        </p>
                                    )}

                                    {/* Contenido del mensaje */}
                                    {msg.media_type ? (
                                        <MediaMessage
                                            logId={msg.id}
                                            mediaType={msg.media_type}
                                            caption={msg.media_caption}
                                            filename={msg.media_filename}
                                        />
                                    ) : (
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                                            {msg.message}
                                        </p>
                                    )}

                                    {/* Timestamp */}
                                    <div className="flex items-center justify-end gap-1 mt-1" >
                                        <span className="text-[10px] text-gray-500">
                                            {formatTime(msg.timestamp)}
                                        </span>
                                        {msg.direction === 'outgoing' && (
                                            <DeliveryStatusIcon status={msg.delivery_status} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-[#F0F2F5] px-4 py-3 border-t border-gray-200">
                {!canReply && (
                    <div className="mb-2 flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
                        <ExclamationCircleIcon className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-orange-700">
                            Han pasado más de 24 horas desde el último mensaje del paciente. WhatsApp ya no permite
                            enviar mensajes de texto libres en esta conversación hasta que el paciente vuelva a escribir.
                        </p>
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <div className="flex-1 bg-white rounded-lg shadow-sm">
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={canReply ? "Escribe un mensaje..." : "Fuera de la ventana de 24h de WhatsApp"}
                            className="w-full px-4 py-2 resize-none focus:outline-none rounded-lg max-h-32 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                            rows={1}
                            maxLength={4096}
                            disabled={sending || !canReply}
                        />
                    </div>
                    <button
                        onClick={handleSend}
                        disabled={!message.trim() || sending || !canReply}
                        className={`
                            p-3 rounded-full transition-all shadow-md
                            ${message.trim() && !sending && canReply
                                ? 'bg-[#25D366] hover:bg-[#128C7E] text-white cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }
                        `}
                    >
                        {sending ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            <PaperAirplaneIcon className="h-5 w-5" />
                        )}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right">
                    {message.length}/4096 • Enter para enviar, Shift+Enter para nueva línea
                </p>
            </div>
        </div>
    );
};

export default ChatView;
